/**
 * Ephemeral UI state. SPEC.md §37.
 *
 * Nothing in here is authoritative and nothing in here survives a resync: open
 * sheets, the keypad draft, the host's "control Sarah" mode and the toast queue.
 * Mixing any of it into the domain model would mean a snapshot could close a
 * player's sheet mid-tap. §37
 *
 * The keypad draft is deliberately *not* domain state even though it looks like
 * it. Digits are composed here and nowhere else until the player commits, which
 * is what makes "20, no 25, no wrong button" free — there is nothing to undo
 * because nothing was sent. §10
 */

import { create } from 'zustand';
import {
  DRINK_CELEBRATION_CYCLES,
  DRINK_CELEBRATION_DURATION_MS,
  UNDO_TOAST_DURATION_MS,
  WINNER_CELEBRATION_CYCLES,
  WINNER_CELEBRATION_DURATION_MS,
} from '@card-counter/shared';

export interface Toast {
  id: string;
  message: string;
  actionLabel?: string;
  onAction?: () => void;
  /** Defaults to the shared undo window; 0 keeps it up until dismissed. §12.1 */
  durationMs?: number;
}

/**
 * The snackbar a report puts up, so it can be taken down again. §12.1, §4.3
 *
 * It lives here rather than beside the action that pushes it because two things
 * take it down and only one of them is that action: the player withdrawing their
 * own report, and the *hand ending* — which arrives on a server frame, at every
 * device at the table, and offers an UNDO the server would refuse. §12.5, §15.2
 */
export const HAND_ENTRY_TOAST = 'hand-entry';

export type ToastInput = Omit<Toast, 'id'> & { id?: string };

/**
 * A number being composed. `digits` is the magnitude as typed — no sign, no
 * leading zeros. There is no sign to compose: only losses are ever reported, and
 * which of the two things a loss does is chosen by which button commits it
 * rather than by a state the draft has to carry. §3.1, §10
 */
export interface KeypadDraft {
  digits: string;
  /**
   * True while `digits` is a value the player has not typed themselves — an
   * amount prefilled from their standing entry. The next digit then replaces it
   * instead of appending, the way a selected field or a calculator behaves.
   *
   * Without this, correcting a committed 57 to 9 gives 579, which is precisely
   * the fiddly editing this keypad exists to remove.
   */
  pristine: boolean;
}

export const EMPTY_KEYPAD_DRAFT: KeypadDraft = { digits: '', pristine: false };

export interface UiStoreState {
  keypadDraft: KeypadDraft;
  historyOpen: boolean;
  playersOpen: boolean;
  hostMenuOpen: boolean;
  /** Every player's, not the host's: an avatar is your own identity. §46, §5.1 */
  avatarPickerOpen: boolean;
  adminPlayerId: string | null;
  /** Set while the host enters scores as another player. §30 */
  controllingPlayerId: string | null;
  toasts: Toast[];
  /** Whatever the table is cheering right now, or null. §4.5, §4.6 */
  celebration: Celebration | null;
  /** The hand boundary currently being drawn, or null. §4.4 */
  explosion: RoundExplosion | null;
  /**
   * How many reports the server has refused as contradicting the hand. §3.2
   *
   * A counter rather than a flag, and that is the whole reason it exists: the
   * refusal is drawn as a CSS animation on the number display, and an animation
   * whose `animation-name` has not changed does not run again. Keyed on a
   * boolean, a player who tapped the same refused button twice would see the
   * flash once and conclude the second tap was dropped — which is exactly the
   * confusion the flash is there to prevent. Every refusal changes the number,
   * so every refusal remounts the element and plays. §6.3, §19
   */
  rejectedEntryCount: number;
}

/**
 * A hand that has just turned over. §4.4, §4.3
 *
 * Half a second of playing cards, and then gone — so unlike a celebration there
 * is nothing here about *who*: a hand ends whether or not anybody won it (§3.2),
 * and a burst that picked a face would be inventing a result on the hands where
 * one player paid everybody.
 *
 * `id` is the round, and it is what makes the effect idempotent: the same hand
 * cannot turn over twice however many times its completion is re-observed. The
 * hand *number* rides along only so the layer can say which hand it drew for,
 * which is the one thing a test can read after half a second has passed. §42.2
 */
export interface RoundExplosion {
  id: string;
  handNumber: number;
  startedAt: number;
}

/**
 * The cheer currently on screen. §4.5, §4.6
 *
 * `id` is the identity of the thing being cheered and the reason this is a
 * record rather than a boolean: it is what makes the effect idempotent. The same
 * hand cannot be celebrated twice however many times its completion is
 * re-observed — by a remount, a StrictMode double-effect, or a resync landing on
 * top of it.
 *
 * Two things are cheered and they share one slot, because they cheer the same
 * way: a hand somebody won, and — in drinking mode — a drink somebody finished.
 * Both dance the player's own avatar on its podium and they differ only in how
 * long they run, so the cycle count rides along rather than being looked up by
 * kind at the point of drawing. A drink landing during a won hand replaces it:
 * whatever just happened is what the table is looking at, and one avatar cannot
 * dance two dances. §4.6
 */
export interface Celebration {
  kind: 'winner' | 'drink';
  /** The round for a won hand; the adjustment row for a finished drink. */
  id: string;
  playerId: string;
  /** Dance cycles, which with the shared cycle length *is* the duration. §60 */
  cycles: number;
  /** Where the animation is in its run, so a remount does not restart it. */
  startedAt: number;
}

export interface UiStoreActions {
  setKeypadDraft(draft: KeypadDraft): void;
  openHistory(): void;
  closeHistory(): void;
  openPlayers(): void;
  closePlayers(): void;
  openHostMenu(): void;
  closeHostMenu(): void;
  openAvatarPicker(): void;
  closeAvatarPicker(): void;
  openPlayerAdmin(playerId: string): void;
  closePlayerAdmin(): void;
  setControllingPlayer(playerId: string | null): void;
  pushToast(toast: ToastInput): string;
  dismissToast(id: string): void;
  /** The server refused a report as contradicting the hand. §3.2, §19 */
  rejectEntry(): void;
  /**
   * Play the winner celebration for a hand, at most once ever. §4.5
   *
   * Returns whether it started, which is what a test asserts on: silently doing
   * nothing is the correct behaviour for a repeat and indistinguishable from a
   * bug without it.
   */
  startWinnerCelebration(input: { roundId: string; winnerPlayerId: string }): boolean;
  /**
   * Cheer a finished drink, on every device at the table. §4.6
   *
   * No latch: a drink is a thing a player does several times a night and every
   * one of them is its own event, so there is nothing to be idempotent about
   * beyond the transport already dropping a duplicate frame. §15.2
   */
  startDrinkCelebration(input: { adjustmentId: string; playerId: string }): boolean;
  /**
   * Draw the hand boundary, at most once per hand ever. §4.4
   *
   * Returns whether it started, for the same reason the celebration does: doing
   * nothing is the correct answer to a repeat and is indistinguishable from a
   * dropped event without it.
   */
  startRoundExplosion(input: { roundId: string; handNumber: number }): boolean;
  /** Puts the layer away once the last card has landed. */
  endRoundExplosion(): void;
  endWinnerCelebration(): void;
  /**
   * Stops a celebration whose player is no longer at the table. §31, §4.5
   *
   * A removed player has no podium to dance on, so the effect is already
   * invisible — but its timer is not, and leaving it running means the *next*
   * thing to be cheered inherits whatever is left of a thirty-second clock that
   * belongs to somebody who has gone. Cleared here, on the frame that removed
   * them, rather than watched for by whatever happens to be rendering. §12.1
   */
  endCelebrationFor(playerId: string): void;
  /** keepToasts survives a session teardown, so "why you were sent back" is
      still on screen after the redirect. §35 */
  reset(options?: { keepToasts?: boolean }): void;
}

export type UiStore = UiStoreState & UiStoreActions;

const EMPTY_STATE: UiStoreState = {
  keypadDraft: EMPTY_KEYPAD_DRAFT,
  historyOpen: false,
  playersOpen: false,
  hostMenuOpen: false,
  avatarPickerOpen: false,
  adminPlayerId: null,
  controllingPlayerId: null,
  toasts: [],
  celebration: null,
  explosion: null,
  rejectedEntryCount: 0,
};

/**
 * The last hand this device has already celebrated.
 *
 * Module scope rather than store state, because it has to outlive `reset()`:
 * ending a game and starting another must not make an old hand celebratable
 * again on the strength of a cleared store. It is one id, not a set — hands
 * complete in order, and the only duplicate worth defending against is a repeat
 * of the completion that just happened. §4.5
 */
let celebratedRoundId: string | null = null;

/**
 * The last hand this device has already drawn a boundary for. §4.4
 *
 * A second latch rather than a share of the first, because the two effects are
 * true of different hands: every completed hand turns over, and only a hand one
 * player was silent for has a winner to cheer (§3.2). One latch would mean a
 * hand nobody won suppressed the *next* hand's burst, or a burst that had
 * already played re-armed the cheer.
 *
 * Module scope for the same reason `celebratedRoundId` is: it has to outlive
 * `reset()`, so ending one game and starting another cannot make an old hand
 * explodable again on the strength of a cleared store.
 */
let explodedRoundId: string | null = null;

/** One celebration runs at a time, so its timer needs only one slot. */
const CELEBRATION_TIMER = 'winner-celebration';

/** How long each kind stays up, which is exactly its pulses. §4.5, §4.6 */
const CELEBRATION_DURATION_MS: Record<Celebration['kind'], number> = {
  winner: WINNER_CELEBRATION_DURATION_MS,
  drink: DRINK_CELEBRATION_DURATION_MS,
};

const timers = new Map<string, ReturnType<typeof setTimeout>>();
let toastCounter = 0;

function clearTimer(id: string): void {
  const timer = timers.get(id);
  if (timer !== undefined) {
    clearTimeout(timer);
    timers.delete(id);
  }
}

/**
 * Puts a cheer on screen and books its end. §4.5, §4.6
 *
 * Timed here rather than in the component, for the same reason a toast is: the
 * effect outlives whatever rendered it, and a roster that unmounts mid-animation
 * — an overlay opening over it, a resync, a remount — must not leave an avatar
 * dancing for the rest of the night. One timer, because one cheer is on screen
 * at a time, and starting another *replaces* it rather than racing it: the
 * previous clock is cleared before the new one is booked, so the winner of hand
 * 12 never stops dancing on hand 11's schedule. §12.1
 */
function startCheer(
  set: (partial: Partial<UiStoreState>) => void,
  get: () => UiStore,
  celebration: Celebration,
): void {
  clearTimer(CELEBRATION_TIMER);
  timers.set(
    CELEBRATION_TIMER,
    setTimeout(() => {
      timers.delete(CELEBRATION_TIMER);
      get().endWinnerCelebration();
    }, CELEBRATION_DURATION_MS[celebration.kind]),
  );
  set({ celebration });
}

export const useUiStore = create<UiStore>()((set, get) => ({
  ...EMPTY_STATE,

  setKeypadDraft: (draft) => set({ keypadDraft: draft }),

  openHistory: () => set({ historyOpen: true }),
  closeHistory: () => set({ historyOpen: false }),

  openPlayers: () => set({ playersOpen: true }),
  closePlayers: () => set({ playersOpen: false }),

  openHostMenu: () => set({ hostMenuOpen: true }),
  closeHostMenu: () => set({ hostMenuOpen: false }),

  openAvatarPicker: () => set({ avatarPickerOpen: true }),
  closeAvatarPicker: () => set({ avatarPickerOpen: false }),

  openPlayerAdmin: (playerId) => set({ adminPlayerId: playerId }),
  closePlayerAdmin: () => set({ adminPlayerId: null }),

  setControllingPlayer: (playerId) => set({ controllingPlayerId: playerId }),

  rejectEntry: () => {
    set((state) => ({ rejectedEntryCount: state.rejectedEntryCount + 1 }));
  },

  pushToast: (toast) => {
    toastCounter += 1;
    const id = toast.id ?? `toast-${String(toastCounter)}`;
    clearTimer(id);
    set((state) => ({
      toasts: [...state.toasts.filter((t) => t.id !== id), { ...toast, id }],
    }));

    // Timed here rather than in a component so a toast still expires if whatever
    // rendered it unmounts mid-countdown. §12.1
    const duration = toast.durationMs ?? UNDO_TOAST_DURATION_MS;
    if (duration > 0) {
      timers.set(
        id,
        setTimeout(() => {
          timers.delete(id);
          get().dismissToast(id);
        }, duration),
      );
    }
    return id;
  },

  dismissToast: (id) => {
    clearTimer(id);
    set((state) => {
      const toasts = state.toasts.filter((t) => t.id !== id);
      return toasts.length === state.toasts.length ? state : { toasts };
    });
  },

  startWinnerCelebration: ({ roundId, winnerPlayerId }) => {
    if (celebratedRoundId === roundId) return false;
    celebratedRoundId = roundId;

    startCheer(set, get, {
      kind: 'winner',
      id: roundId,
      playerId: winnerPlayerId,
      cycles: WINNER_CELEBRATION_CYCLES,
      startedAt: Date.now(),
    });
    return true;
  },

  startDrinkCelebration: ({ adjustmentId, playerId }) => {
    startCheer(set, get, {
      kind: 'drink',
      id: adjustmentId,
      playerId,
      cycles: DRINK_CELEBRATION_CYCLES,
      startedAt: Date.now(),
    });
    return true;
  },

  startRoundExplosion: ({ roundId, handNumber }) => {
    if (explodedRoundId === roundId) return false;
    explodedRoundId = roundId;

    /*
      No timer booked here, unlike a cheer. The layer knows when its own last
      card lands — it is the thing that started them — so it calls
      `endRoundExplosion` itself. Booking a second clock in the store would be
      two answers to "is it over yet", and the wrong one would either cut a card
      off mid-flight or leave fifty compositor layers alive under the keypad.
    */
    set({ explosion: { id: roundId, handNumber, startedAt: Date.now() } });
    return true;
  },

  endRoundExplosion: () => {
    set((state) => (state.explosion === null ? state : { explosion: null }));
  },

  endWinnerCelebration: () => {
    clearTimer(CELEBRATION_TIMER);
    set((state) => (state.celebration === null ? state : { celebration: null }));
  },

  endCelebrationFor: (playerId) => {
    if (get().celebration?.playerId !== playerId) return;
    get().endWinnerCelebration();
  },

  reset: (options = {}) => {
    const keptToasts = options.keepToasts ? get().toasts : [];
    if (!options.keepToasts) {
      for (const id of [...timers.keys()]) clearTimer(id);
    }
    set({ ...EMPTY_STATE, toasts: keptToasts });
  },
}));

// --- Hooks -------------------------------------------------------------------

export const useKeypadDraft = (): KeypadDraft => useUiStore((s) => s.keypadDraft);
export const useToasts = (): Toast[] => useUiStore((s) => s.toasts);
export const useControllingPlayerId = (): string | null =>
  useUiStore((s) => s.controllingPlayerId);
export const useAdminPlayerId = (): string | null => useUiStore((s) => s.adminPlayerId);

export const useCelebration = (): Celebration | null => useUiStore((s) => s.celebration);
export const useRoundExplosion = (): RoundExplosion | null => useUiStore((s) => s.explosion);

/** Convenience for the action layer, which runs outside React. */
export function pushToast(toast: ToastInput): string {
  return useUiStore.getState().pushToast(toast);
}

/**
 * Called from the socket layer the moment the server confirms a completion, so
 * every device starts within a round-trip of every other. §4.5, §15.3
 */
export function startWinnerCelebration(input: {
  roundId: string;
  winnerPlayerId: string;
}): boolean {
  return useUiStore.getState().startWinnerCelebration(input);
}

/**
 * Called from the socket layer the moment the server confirms a drink, so every
 * phone at the table cheers within a round-trip of every other. §4.6, §15.3
 */
export function startDrinkCelebration(input: {
  adjustmentId: string;
  playerId: string;
}): boolean {
  return useUiStore.getState().startDrinkCelebration(input);
}

/**
 * Called from the socket layer the moment the server confirms a hand has ended,
 * so every phone at the table turns over within a round-trip of every other.
 * §4.4, §15.3
 */
export function startRoundExplosion(input: { roundId: string; handNumber: number }): boolean {
  return useUiStore.getState().startRoundExplosion(input);
}

/** Called by the layer itself once its last card has landed. §4.4 */
export function endRoundExplosion(): void {
  useUiStore.getState().endRoundExplosion();
}

/**
 * Called from the socket layer when the host removes somebody, so a cheer never
 * outlives the player it belongs to. §31, §4.5
 */
export function endCelebrationFor(playerId: string): void {
  useUiStore.getState().endCelebrationFor(playerId);
}

/** Test seam: lets a spec replay a hand the module has already celebrated. */
export function forgetCelebratedRounds(): void {
  celebratedRoundId = null;
  explodedRoundId = null;
}

export function dismissToast(id: string): void {
  useUiStore.getState().dismissToast(id);
}
